import React, { useState, useRef, useEffect } from "react";
import { Loader2, ImageOff } from "lucide-react";

/**
 * ImageWithLoader Component
 * Displays a sleek animated skeleton and spinner while the image is loading,
 * then smoothly fades the image in upon completion.
 */
export default function ImageWithLoader({
  src,
  alt = "",
  className = "",
  wrapperClassName = "",
  fallbackSrc,
  aspectRatio,
  showSpinner = true,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  // Check if image was already cached/loaded by the browser
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  // Reset status when src changes
  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleError = (e) => {
    if (fallbackSrc && src !== fallbackSrc) {
      e.currentTarget.src = fallbackSrc;
    } else {
      setError(true);
      setLoaded(true);
    }
  };

  return (
    <div
      className={`relative overflow-hidden bg-zinc-100/80 ${wrapperClassName}`}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {/* Animated Loading Skeleton */}
      {!loaded && !error && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-100/90 overflow-hidden">
          {/* Shimmer sweep effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent -translate-x-full animate-shimmer" />

          {/* Minimalist animated spinner */}
          {showSpinner && (
            <div className="relative z-10 flex flex-col items-center gap-2 p-3 bg-white/70 backdrop-blur-sm rounded-full shadow-sm border border-zinc-200/60">
              <Loader2 className="w-5 h-5 text-zinc-700 animate-spin" />
            </div>
          )}
        </div>
      )}

      {/* Fallback Error State */}
      {error ? (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 bg-zinc-100 text-zinc-400">
          <ImageOff className="w-6 h-6 mb-1.5 opacity-60" />
          <span className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500">
            Image preview unavailable
          </span>
        </div>
      ) : (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-all duration-700 ease-out ${
            loaded ? "opacity-100 scale-100" : "opacity-0 scale-[1.02] filter blur-[2px]"
          } ${className}`}
          {...props}
        />
      )}
    </div>
  );
}
